<template>
    <div>
        <Toolbar>
            <template>
                <ToolbarLink :href="$route('brands.create')" v-if="Permission.CREATE_BRANDS">
                    <i class="fas fa-plus-square"/>
                    {{ __('actions.create_brand') }}
                </ToolbarLink>
            </template>
        </Toolbar>
        <Card>
            <div class="d-flex flex-row">
                <div class="flex-grow-1 d-flex flex-row">
                    <div class="d-flex flex-column justify-content-center">
                        <toggle :active="ownedOnly" @change="showUserBrandToggleChanged"/>
                    </div>
                    <div class="ml-2 d-flex flex-column justify-content-center">Nur Marken im Besitz anzeigen</div>
                </div>
                <div class="d-flex flex-row">
                    <div class="d-flex flex-column justify-content-center mr-2">Suchen</div>
                    <input type="text" class="form-control" v-model="search" placeholder="Markenname">
                </div>
            </div>

            <div class="mt-5">
                <div class="table-responsive">
                    <table class="table table-hover table-vertical-center table-head-bg">
                        <thead>
                        <tr>
                            <th class="w-15">
                                <TableSort field="name" :value="serverParams.sort" @input="onSortChange">
                                    {{ __('general.name') }}
                                </TableSort>
                            </th>
                            <th class="w-15">
                                {{ __('general.logo') }}
                            </th>
                            <th class="text-center">
                                <TableSort field="distribution" :value="serverParams.sort" @input="onSortChange">
                                    {{ __('general.product.distribution') }}
                                </TableSort>
                            </th>
                            <th class="text-center" v-show="Permission.UPDATE_BRANDS">
                                <TableSort field="products_count" :value="serverParams.sort" @input="onSortChange">
                                    {{ __('general.product.multi') }}
                                </TableSort>
                            </th>
                            <th class="text-right user-select-none">{{ __('general.actions') }}</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="brand in rows" :key="brand.id">
                            <td class="w-15">
                                {{ brand.name }}
                            </td>
                            <td class="w-15">
                                <div v-if="brand.table">
                                    <img :src="brand.table" :alt="brand.name"
                                         style="filter:saturate(0%); height: 1.25em;">
                                </div>
                                <span v-else class="text-muted line-height-sm">Nicht vorhanden</span>
                            </td>
                            <td class="text-center">
                                <div v-if="userOwns(brand) || Permission.UPDATE_BRANDS" class="d-flex flex-column justify-content-center h-3em">
                                    <span v-if="brand.distribution === 'all'" class="text-muted">
                                        <i class="fas fa-check mr-1"/>
                                        <b>Alle</b>
                                    </span>
                                    <span v-else-if="brand.distribution === 'limited'" class="text-warning">
                                        <i class="fas fa-exclamation-triangle mr-1"/>
                                        <b>Beschränkt</b>
                                    </span>
                                    <span v-else class="text-danger">
                                        <i class="fas fa-times-circle mr-1"/>
                                        <b>Deaktiviert</b>
                                    </span>
                                </div>
                                <span v-else><i class="fas fa-eye-slash"/></span>
                            </td>
                            <td class="text-center" v-show="Permission.UPDATE_BRANDS">
                                {{ brand.products_count }}
                            </td>
                            <td class="text-right user-select-none">
                                <!-- Actions -->
                                <actions class="user-select-none">
                                    <a v-if="showClaimButton(brand)"
                                       :href="$route('brands.claim', [brand.uuid])"
                                       class="btn btn-sm btn-hover-info btn-secondary">
                                        <i class="fas fa-link"/>
                                        Inhaberschaft beantragen
                                    </a>
                                    <a v-if="showEditButton(brand)"
                                       :href="$route('brands.edit', [brand.uuid])"
                                       class="btn btn-sm btn-hover-info btn-secondary ml-2">
                                        <i class="fas fa-pen"/>
                                        Bearbeiten
                                    </a>
                                </actions>
                            </td>
                        </tr>
                        </tbody>
                        <tfoot>
                        <tr>
                            <td colspan="5">
                                <pagination :loading="loading"
                                            :pagination="pagination"
                                            @changePage="pageChanged"
                                            @changePerPage="perPageChanged"
                                            class="user-select-none mt-2">
                                </pagination>
                            </td>
                        </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </Card>
    </div>
</template>

<script>
import Actions from '@components/TableComponents/Actions';
import ActionButton from '@components/TableComponents/ActionButton';
import Pagination from '@components/TableComponents/Pagination';
import Toggle from '@components/Toggle';
import {debounce} from '@composables/debounce';
import Toolbar from '@components/Toolbar.vue';
import ToolbarLink from '@components/ToolbarLink.vue';
import Card from '@components/Cards/Card.vue';
import User from '@components/TableComponents/User.vue';
import TableSort from '@components/Table/TableSort.vue';
import DateTime from '@components/TableComponents/DateTime.vue';
import Agency from '@components/TableComponents/Agency.vue';
import BigNumber from '@components/TableComponents/BigNumber.vue';
import {rewriteUrlNew} from '@composables/rewriteUrlNew';
import {PermissionsMixin} from '@js/permissions';

export default {
    components: {
        BigNumber, Agency, DateTime, TableSort, User,
        Card,
        ToolbarLink, Toolbar,
        Actions,
        ActionButton,
        Pagination,
        Toggle,
    },

    mixins: [
        PermissionsMixin,
    ],

    props: {
        init: Object,
        userBrands: Array,
        ownedOnly: Boolean,
        canClaimBrands: Boolean,
    },

    data() {
        return {
            loading: false,
            rows: [],
            pagination: null,
            search: '',
            showOnlyUserBrands: false,
            serverParams: {
                sort: {
                    field: '',
                    type: '',
                },
                page: 1,
                perPage: 10,
            },
        };
    },

    mounted() {
        this.serverParams.page = this.init.page;
        this.serverParams.perPage = this.init.perPage;
        this.serverParams.sort = this.init.sort;
        this.search = this.init.search;
        this.showOnlyUserBrands = this.ownedOnly;
        this.loadItems();
    },

    methods: {
        async loadItems() {
            this.loading = true;
            let {data} = await axios.get(this.$route('brands.data', {
                page: this.serverParams.page,
                per_page: this.serverParams.perPage,
                search: this.search,
                sort: this.serverParams.sort,
                owned: this.showOnlyUserBrands,
                with_has_product_import_settings: true,
            }));

            this.rows = data.data;
            delete data.data;
            this.pagination = data;
            this.loading = false;
        },

        onSortChange(sortOptions) {
            this.serverParams.sort = sortOptions;
            this.setPage(1);
            rewriteUrlNew('sort', sortOptions);
            this.loadItems();
        },

        perPageChanged(n) {
            this.serverParams.perPage = n;
            rewriteUrlNew('per_page', this.serverParams.perPage);
            this.loadItems();
        },

        userOwns(brand) {
            return this.userBrands.includes(brand.uuid);
        },

        showClaimButton(brand) {
            return this.canClaimBrands && !this.userOwns(brand);
        },

        showEditButton(brand) {
            return this.Permission.UPDATE_BRANDS || this.userOwns(brand);
        },

        showUserBrandToggleChanged(newState) {
            this.showOnlyUserBrands = newState;
            this.setPage(1);
            rewriteUrlNew('owned', newState);
            this.loadItems();
        },

        setPage(n) {
            if (this.serverParams.page === n) {
                return;
            }

            this.serverParams.page = n;
            rewriteUrlNew('page', n);
        },

        pageChanged(n) {
            this.setPage(n);
            this.loadItems();
        },
    },

    watch: {
        search: debounce(function(newVal) {
            this.loadItems();
        }),
    },
};
</script>
